import React from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const tocItems = [
  { id: "summary", label: "SUMMARY OF KEY POINTS" },
  { id: "toc", label: "TABLE OF CONTENTS" },
  { id: "infocollect", label: "1. WHAT INFORMATION DO WE COLLECT?" },
  { id: "infouse", label: "2. HOW DO WE PROCESS YOUR INFORMATION?" },
  { id: "whoshare", label: "3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?" },
  { id: "cookies", label: "4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?" },
  { id: "sociallogins", label: "5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?" },
  { id: "intltransfers", label: "6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?" },
  { id: "inforetain", label: "7. HOW LONG DO WE KEEP YOUR INFORMATION?" },
  { id: "infominors", label: "8. DO WE COLLECT INFORMATION FROM MINORS?" },
  { id: "privacyrights", label: "9. WHAT ARE YOUR PRIVACY RIGHTS?" },
  { id: "dnt", label: "10. CONTROLS FOR DO-NOT-TRACK FEATURES" },
  { id: "policyupdates", label: "11. DO WE MAKE UPDATES TO THIS NOTICE?" },
  { id: "contact", label: "12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" },
  { id: "request", label: "13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?" },
];

export default function PrivacyPolicyPage() {
  return (
    <>
    <Navbar />
    <div className="policy-page">
      <div className="policy-container">
        <header className="policy-header">
          <h1>Privacy Policy</h1>
          <p className="policy-updated">
            <strong>Last updated:</strong> January 01, 2026
          </p>
          <p>
            This Privacy Notice for <strong>A1 Bakery and Sweets</strong> describes how and why we may access,
            collect, store, use, and/or share your personal information when you use our services,
            including when you visit{" "}
            <a
              href="http://www.a1bakeryandsweets.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.a1bakeryandsweets.com
            </a>
            .
          </p>
          <p>
            Reading this Privacy Policy will help you understand your privacy rights and choices. If you do not agree
            with our policies and practices, please do not use our services.
          </p>
        </header>

        <section className="policy-toc">
          <h2>Table of Contents</h2>
          <ul>
            {tocItems.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ul>
        </section>

        <section id="summary" className="policy-section">
          <h2>Summary of Key Points</h2>
          <p>
            This summary provides key points from our Privacy Policy, but you can find more detail by reviewing the
            relevant sections below.
          </p>
          <p>
            We may process personal information depending on how you interact with our website, the products and
            features you use, and the choices you make.
          </p>
          <p>
            We do not process sensitive personal information unless specifically required and lawfully permitted.
          </p>
          <p>
            We may collect limited information from third parties such as public databases, marketing partners, social
            media platforms, or service providers where applicable.
          </p>
          <p>
            We process information to provide, improve, and administer our services, communicate with you, prevent
            fraud, maintain security, and comply with legal obligations.
          </p>
          <p>
            Depending on your location, you may have privacy rights relating to access, correction, deletion, and
            other controls over your personal information.
          </p>
        </section>

        <section id="toc" className="policy-section">
          <h2>Table of Contents</h2>
          <p>
            Use the links above to jump directly to any section of this Privacy Policy.
          </p>
        </section>

        <section id="infocollect" className="policy-section">
          <h2>1. What Information Do We Collect?</h2>

          <h3>Personal information you disclose to us</h3>
          <p>
            We collect personal information that you voluntarily provide to us when you register on the website,
            place an order, contact us, request information about our products or services, or otherwise interact
            with us.
          </p>
          <p>
            The personal information we collect may include your name, phone number, email address, billing address,
            shipping address, account credentials, and any other information you choose to provide.
          </p>
          <p>
            All personal information you provide must be true, complete, and accurate, and you must notify us of any
            changes to that information.
          </p>

          <h3>Sensitive information</h3>
          <p>
            We do not intentionally process sensitive personal information unless required for a lawful business
            purpose and supported by applicable law.
          </p>

          <h3>Information automatically collected</h3>
          <p>
            We automatically collect certain information when you visit, use, or navigate our website. This
            information may include your IP address, browser type, device characteristics, operating system, language
            preferences, referring URLs, country, approximate location, and information about how and when you use
            our services.
          </p>
          <p>
            This information does not usually reveal your specific identity, but it may help us maintain security,
            improve performance, troubleshoot issues, and understand website usage.
          </p>
          <p>
            Like many businesses, we also collect information through cookies and similar tracking technologies.
          </p>
        </section>

        <section id="infouse" className="policy-section">
          <h2>2. How Do We Process Your Information?</h2>
          <p>
            We process your personal information to provide, improve, and administer our services, fulfill orders,
            manage accounts, communicate with you, respond to inquiries, send service-related messages, and maintain
            the security of our platform.
          </p>
          <p>
            We may also process your information for analytics, fraud prevention, legal compliance, recordkeeping,
            internal business operations, and other purposes where we have a valid legal basis or your consent.
          </p>
          <p>
            In some cases, we may process your information to send promotional communications, manage customer
            relationships, or personalize website content and product recommendations.
          </p>
        </section>

        <section id="whoshare" className="policy-section">
          <h2>3. When and With Whom Do We Share Your Personal Information?</h2>
          <p>
            We may share personal information in specific situations and with selected third parties where necessary
            for business operations or legal compliance.
          </p>
          <ul>
            <li>Business transfers, such as a merger, sale of assets, financing, or acquisition.</li>
            <li>Affiliates, subsidiaries, or related entities operating under common control.</li>
            <li>Business partners who help us offer products, services, promotions, payments, logistics, or support.</li>
            <li>Service providers who perform hosting, analytics, communications, payment processing, and technical support.</li>
            <li>Authorities, regulators, or legal bodies where disclosure is required by law or necessary to protect rights and safety.</li>
          </ul>
        </section>

        <section id="cookies" className="policy-section">
          <h2>4. Do We Use Cookies and Other Tracking Technologies?</h2>
          <p>
            Yes. We may use cookies, pixels, web beacons, and similar tracking technologies to collect and store
            information when you interact with our services.
          </p>
          <p>
            These technologies help us maintain website functionality, remember your preferences, keep the site
            secure, analyze traffic, improve user experience, and support marketing or advertising activities where
            applicable.
          </p>
          <p>
            Third parties and service providers may also use tracking technologies for analytics, measurement,
            advertising, or cart reminder features, subject to your choices and applicable law.
          </p>
        </section>

        <section id="sociallogins" className="policy-section">
          <h2>5. How Do We Handle Your Social Logins?</h2>
          <p>
            If you choose to register or sign in using a third-party social media account, such as Google or another
            supported provider, we may receive profile information from that provider.
          </p>
          <p>
            The information we receive may include your name, email address, profile image, and any other information
            you choose to make available through that provider.
          </p>
          <p>
            We use this information only for the purposes described in this Privacy Policy or otherwise made clear to
            you at the time of login. We do not control how third-party social platforms process your information.
          </p>
        </section>

        <section id="intltransfers" className="policy-section">
          <h2>6. Is Your Information Transferred Internationally?</h2>
          <p>
            Your information may be transferred to, stored in, and processed in countries other than your own,
            depending on the location of our servers, service providers, or business operations.
          </p>
          <p>
            If you access our services from a region with data protection laws that differ from those in the country
            where your data is processed, we will take reasonable steps to protect your information in accordance
            with this Privacy Policy and applicable legal requirements.
          </p>
        </section>

        <section id="inforetain" className="policy-section">
          <h2>7. How Long Do We Keep Your Information?</h2>
          <p>
            We keep your personal information only for as long as necessary to fulfill the purposes described in this
            Privacy Policy, unless a longer retention period is required or permitted by law.
          </p>
          <p>
            This may include retaining information for tax, accounting, fraud prevention, dispute resolution, legal
            compliance, and internal recordkeeping.
          </p>
          <p>
            When we no longer have a legitimate business need to process your personal information, we will delete,
            anonymize, or securely isolate it where deletion is not immediately possible.
          </p>
        </section>

        <section id="infominors" className="policy-section">
          <h2>8. Do We Collect Information From Minors?</h2>
          <p>
            We do not knowingly collect, solicit, or market to children under 18 years of age.
          </p>
          <p>
            By using our services, you represent that you are at least 18 years old or that you are the parent or
            legal guardian of a minor and consent to that minor’s use of the services.
          </p>
          <p>
            If we learn that personal information from a child under 18 has been collected without proper consent, we
            will take reasonable steps to delete that information.
          </p>
        </section>

        <section id="privacyrights" className="policy-section">
          <h2>9. What Are Your Privacy Rights?</h2>
          <p>
            Depending on your country, state, or province of residence, you may have rights under applicable privacy
            laws relating to your personal information.
          </p>
          <p>
            These rights may include the right to request access to the personal information we hold about you, request
            correction of inaccurate information, request deletion, object to certain processing, or withdraw consent
            where consent is the legal basis for processing.
          </p>
          <p>
            If you would like to exercise any of these rights, you may contact us using the details provided below.
          </p>

          <h3>Withdrawing consent</h3>
          <p>
            If we rely on your consent to process your personal information, you may withdraw that consent at any
            time. Withdrawal does not affect the lawfulness of processing carried out before the withdrawal.
          </p>

          <h3>Account information</h3>
          <p>
            You may review, update, or request deletion of your account information by contacting us directly or by
            using the tools available in your account, where provided.
          </p>
        </section>

        <section id="dnt" className="policy-section">
          <h2>10. Controls for Do-Not-Track Features</h2>
          <p>
            Most web browsers and some mobile operating systems include a Do-Not-Track feature or setting you can
            activate to signal your privacy preference not to have data about your browsing activities monitored and
            collected.
          </p>
          <p>
            At this time, no uniform technology standard for recognizing and implementing Do-Not-Track signals has
            been finalized, so we do not currently respond to such signals automatically.
          </p>
        </section>

        <section id="policyupdates" className="policy-section">
          <h2>11. Do We Make Updates to This Notice?</h2>
          <p>
            Yes. We may update this Privacy Policy from time to time to remain compliant with relevant laws, reflect
            operational changes, or explain new data practices.
          </p>
          <p>
            The updated version will be identified by a revised “Last updated” date at the top of this page. Where
            required, we may also provide additional notice of material changes.
          </p>
        </section>

        <section id="contact" className="policy-section">
          <h2>12. How Can You Contact Us About This Notice?</h2>
          <p>
            If you have questions or comments about this Privacy Policy, you may contact us using the details below.
          </p>
          <p><strong>A1 Bakery and Sweets</strong></p>
          <p>Phone: 9751336638</p>
          <p>
            Email:{" "}
            <a href="mailto:a1foodfactory2008@gmail.com">
              a1foodfactory2008@gmail.com
            </a>
          </p>
          <p>
            Website:{" "}
            <a
              href="http://www.a1bakeryandsweets.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.a1bakeryandsweets.com
            </a>
          </p>
          <p>Country: India</p>
        </section>

        <section id="request" className="policy-section">
          <h2>13. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
          <p>
            Based on the laws applicable to your location, you may have the right to request access to the personal
            information we collect from you, request correction of inaccuracies, or request deletion of your personal
            information.
          </p>
          <p>
            You may also have the right to withdraw consent to our processing of your personal information in certain
            cases.
          </p>
          <p>
            To make a request, please contact us at{" "}
            <a href="mailto:a1foodfactory2008@gmail.com">
              a1foodfactory2008@gmail.com
            </a>
            . We will review and respond in accordance with applicable data protection laws.
          </p>
        </section>
      </div>
    </div>
    <Footer />
    </>
  );
}